import { useEffect, useState } from 'react';
import { CalendarPlus, Check, ScrollText } from 'lucide-react';
import type { SittingGame } from '../net/sitting.js';
import { trackClient } from '../net/telemetry.js';
import { QuietAction } from './QuietAction.js';
import { humanSeats, isHotSitting, nextShowAnnouncement, playbillText } from './playbill.js';

/**
 * NextShowStrip — the END of a HOT sitting, and the one moment a guild is
 * willing to act on being one (issue #55).
 *
 * The measured shape of the good week (7d to 2026-09-06): 70 completed games in
 * FOUR rooms, one of them 18 games in 59 minutes — and 17 of 122 guilds have
 * ever come back. Hot tables vanish. The Herald (#2) is the bet that fixes
 * that, and it is blocked on a bot token; this is its Phase 0, testing the
 * premise the token would be spent on: at the moment their table is hottest,
 * will a host paste a pre-written line into their own channel?
 *
 * THREE GATES, all of them deliberate:
 *   - END only (the component is mounted by the END screens alone),
 *   - the HOST only (nobody else can book the guild's next night),
 *   - a HOT sitting only — `HOT_SITTING_GAMES` (2) finished games watched in
 *     this room, this visit (net/sitting.ts). After one game there is no
 *     "tonight" to recap and no evidence the guild stayed.
 *
 * ACTION HIERARCHY (issue #29): two QUIET copy actions in their own container.
 * Not a third pill, not a second gold button — the rematch stays the screen's
 * one primary, and this strip lives in the content flow beside the community
 * link rather than in the action bar. Both END surfaces render this ONE
 * component (never two near-copies — same doctrine as StallNudge/CommunityCTA).
 *
 * The sitting log arrives as a PROP rather than off `useRoom` here, and that is
 * deliberate: every rule survey in this package renders screens through
 * `renderToStaticMarkup`, where zustand serves its INITIAL state (v5 passes
 * `getInitialState` as the server snapshot), so a store-reading gate would make
 * this strip invisible to R-001 and the action-hierarchy survey. Views reach
 * screens as props everywhere else in this codebase for the same reason; the
 * store read lives one level up, in the phase routers.
 */

/** Which END surface the strip is mounted on — rides along on both beacons. */
export type NextShowSurface = 'phone' | 'desktop';

/** How long a "Copied" confirmation stays up before settling back. */
const COPIED_MS = 2400;

type Copied = 'next-show' | 'playbill' | null;

export function NextShowStrip({
  isHost,
  sitting,
  roster,
  /** Extra root styles (surface spacing/stacking) — applied only when it renders. */
  style,
  surface,
}: {
  isHost: boolean;
  /** Every game this room has finished this visit (net/sitting.ts). */
  sitting: SittingGame[];
  /** The room's cumulative ledger — the Playbill's closing standing. Troupe
   *  seats are filtered out here, not by the caller (see humanSeats). */
  roster: { name: string; score: number; isAi?: boolean }[];
  surface: NextShowSurface;
  style?: React.CSSProperties;
}) {
  // Both texts are about the PEOPLE at the table: a troupe seat's disclosure
  // does not survive a paste into someone's channel (see humanSeats).
  const humans = humanSeats(roster);
  const players = humans.length;
  const [copied, setCopied] = useState<Copied>(null);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(null), COPIED_MS);
    return () => clearTimeout(t);
  }, [copied]);

  const hot = isHost && isHotSitting(sitting.length);
  if (!hot) return null;

  /**
   * Copy, then beacon. A blocked clipboard (an old browser, a hostile
   * permission policy) leaves the label alone and fires nothing — a copy that
   * did not happen must not be counted, since the whole experiment is a count
   * of copies.
   */
  async function copy(which: Exclude<Copied, null>, text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }
    setCopied(which);
    trackClient(which === 'next-show' ? 'next_show_copied' : 'playbill_copied', {
      surface,
      games: sitting.length,
      players,
    });
  }

  return (
    <div
      className="rm-next-show"
      style={{
        background: 'linear-gradient(180deg, rgba(20,12,7,0.82) 0%, rgba(11,7,5,0.9) 100%)',
        border: '1px solid var(--gold-2, #7a4c1e)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-1)',
        textAlign: 'center',
        ...style,
      }}
    >
      {/* The narrator's aside, in the same display/gold treatment the lobby's
          own aside uses (ui/BiggerShowCTA.tsx) — one voice across the app. */}
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-display)',
          fontSize: 17,
          letterSpacing: '0.04em',
          color: 'var(--gold-5, #e6b866)',
        }}
      >
        Your guild stayed for {sitting.length} games. Book the next show.
      </p>
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-ui)',
          fontSize: 11,
          lineHeight: 1.4,
          color: 'var(--text-tertiary)',
        }}
      >
        Paste either into your channel — nothing is posted for you.
      </p>
      {/* Two quiet controls, one container: the cluster stays at two (R-001)
          and carries no primary fill (actionHierarchy). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 'var(--space-1)' }}>
        <QuietAction
          ariaLabel="Copy a channel announcement for the next show"
          onClick={() => void copy('next-show', nextShowAnnouncement(sitting, players))}
        >
          <span style={LABEL}>
            {copied === 'next-show' ? <Check size={14} aria-hidden /> : <CalendarPlus size={14} aria-hidden />}
            {copied === 'next-show' ? 'Copied — paste it in your channel' : 'Book the next show'}
          </span>
        </QuietAction>
        <QuietAction
          ariaLabel="Copy tonight's playbill"
          onClick={() =>
            void copy(
              'playbill',
              playbillText(sitting, humans.map((p) => ({ name: p.name, score: p.score }))),
            )
          }
        >
          <span style={LABEL}>
            {copied === 'playbill' ? <Check size={14} aria-hidden /> : <ScrollText size={14} aria-hidden />}
            {copied === 'playbill' ? 'Copied — paste it in your channel' : 'Copy the Playbill'}
          </span>
        </QuietAction>
      </div>
    </div>
  );
}

/** Icon + label inside the quiet control, on one line. */
const LABEL: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
